// /coaches/:slug — public read-only coach profile (Tier 4, rev 2).
//
// Display layer only: no activity feed, no posts, no follower graph, no
// messaging. Layout: avatar → name + credential_headline → verified badge
// → FIBA license badge (agent-style "displayed and checkable, not verified
// by us" treatment) → About (bio) → current org + education → social icons.
//
// Migration 0023 removed the cover banner entirely — column, upload path and
// render. Do not reintroduce it without the column.
//
// Photo fallbacks are non-negotiable (R2/api-sports URLs 404 routinely):
//   - avatar → the shared <Avatar> component (onError → initials tile)

import { useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { CircleCheck } from 'lucide-react'
import Avatar from '../components/Avatar'
import FibaBadge from '../components/profile/FibaBadge'
import SocialIcons from '../components/profile/SocialIcons'
import {
  CoachContactCard,
  type CoachContactValues,
} from '../components/profile/CoachContact'
import { useSession } from '../lib/auth'
import { apiJson } from '../lib/api'

interface CoachProfileResponse {
  coach: {
    id: string
    slug: string
    name: string
    credential_headline: string | null
    bio: string | null
    current_team: string | null
    country: string | null
    role: string | null
    education: string | null
    // Migration 0022 — optional in the type only so a cached pre-0022
    // response can't crash the render.
    education_level?: string | null
    fiba_cert_number: string | null
    is_verified: boolean
    photo_url: string | null
    instagram_url: string | null
    youtube_url: string | null
    facebook_url: string | null
    created_at: string
    // Migration 0018 — mutually exclusive, server-shaped:
    // `contact` only for Pro-coach viewers; `contact_locked` otherwise.
    contact?: CoachContactValues
    contact_locked?: boolean
    // Migration 0021 — public, never gated. Optional in the type only so a
    // cached pre-0021 response can't crash the render.
    languages?: string[]
  }
}

interface MeResponse {
  profile: { role: string | null }
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; data: CoachProfileResponse }

export default function CoachProfile() {
  const { slug } = useParams()
  const { data: session } = useSession()
  const [state, setState] = useState<State>({ kind: 'loading' })
  // Migration 0018 — whether the signed-in viewer is a coach. A coach
  // seeing the locked state is necessarily FREE (Pro would have received
  // values), so this flag upgrades the teaser label into a /pricing link
  // (mirrors the 429 sync-cap upsell). Purely presentational — the server
  // already shaped the payload.
  const [viewerIsCoach, setViewerIsCoach] = useState(false)

  useEffect(() => {
    if (!session?.user) {
      setViewerIsCoach(false)
      return
    }
    let cancelled = false
    apiJson<MeResponse>('/api/me')
      .then((me) => {
        if (!cancelled) setViewerIsCoach(me.profile.role === 'coach')
      })
      .catch(() => {
        if (!cancelled) setViewerIsCoach(false)
      })
    return () => {
      cancelled = true
    }
  }, [session])

  useEffect(() => {
    let cancelled = false
    if (!slug) {
      setState({ kind: 'error', message: 'no_slug' })
      return
    }
    async function load() {
      try {
        const res = await fetch(`/api/coaches/${encodeURIComponent(slug!)}`)
        if (cancelled) return
        if (res.status === 404) {
          setState({ kind: 'error', message: 'Coach not found.' })
          return
        }
        if (!res.ok) {
          setState({ kind: 'error', message: `Request failed (${res.status})` })
          return
        }
        const data = (await res.json()) as CoachProfileResponse
        if (cancelled) return
        setState({ kind: 'ready', data })
      } catch (e) {
        if (cancelled) return
        setState({
          kind: 'error',
          message: e instanceof Error ? e.message : 'network_error',
        })
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [slug])

  if (state.kind === 'loading') {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10 text-hoop-text-3 text-sm">
        Loading…
      </div>
    )
  }
  if (state.kind === 'error') {
    return (
      <div className="max-w-4xl mx-auto px-6 py-10">
        <p className="text-hoop-text-2">{state.message}</p>
      </div>
    )
  }

  const { coach } = state.data
  const orgLine = [coach.role, coach.current_team, coach.country]
    .filter(Boolean)
    .join(' · ')

  return (
    <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
      <header className="bg-hoop-surface border border-hoop-border rounded-lg overflow-hidden">
        {/* Migration 0023 — the cover banner is gone. The avatar row carried
            `-mt-10` to overlap the banner's bottom edge and the container had
            `pb-6` only; with no banner to overlap, that offset would pull the
            avatar up out of the card. Symmetric `p-6` and no negative margin
            replace it, so the header reads deliberately rather than leaving a
            banner-shaped hole. */}
        <div className="p-6">
          <div className="flex items-end gap-5">
            <Avatar
              src={coach.photo_url}
              name={coach.name}
              className="w-20 h-20 rounded-full border-4 border-hoop-surface flex-shrink-0"
              textClassName="text-xl"
            />
            <div className="min-w-0 pb-1">
              <div className="text-xs uppercase tracking-wider text-hoop-text-3">
                Coach
              </div>
            </div>
          </div>
          <div className="mt-3 min-w-0">
            <div className="flex items-center gap-2.5 flex-wrap">
              <h1 className="text-3xl font-bold text-hoop-text">
                {coach.name}
              </h1>
              {coach.is_verified && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-hoop-green-bg text-hoop-green">
                  <CircleCheck className="w-3 h-3" />
                  Platform Verified
                </span>
              )}
            </div>
            {coach.credential_headline && (
              <p className="text-hoop-text-2 mt-1">
                {coach.credential_headline}
              </p>
            )}
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-hoop-text-2">
              {/* Agent-style license treatment: displayed + checkable via
                  FIBA's public search, not verified by us. */}
              <FibaBadge licenseNumber={coach.fiba_cert_number} />
              {orgLine && (
                <span className="text-hoop-text-3">{orgLine}</span>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* A2 — was md:grid-cols-3 with a col-span-2 main column, giving Contact
          only a third of the width, which is why a long email still wrapped.
          5 columns split 3/2 gives the Contact card 40% instead. */}
      <div className="grid md:grid-cols-5 gap-6">
        <div className="md:col-span-3 space-y-6">
          {coach.bio && (
            <Card title="About">
              <p className="text-sm text-hoop-text-2 whitespace-pre-line">
                {coach.bio}
              </p>
            </Card>
          )}
          {/* Migration 0021 — languages joins the Background gate. The Card
              hides on VALUES, not on the object's existence: an empty list
              contributes nothing, so a coach with no languages sees no
              heading and no stray label (same rule the Contact card follows). */}
          {(coach.current_team ||
            coach.education ||
            coach.education_level ||
            (coach.languages?.length ?? 0) > 0) && (
            <Card title="Background">
              <dl className="space-y-2 text-sm">
                {coach.current_team && (
                  <div className="flex items-baseline gap-3">
                    <dt className="text-hoop-text-3 shrink-0 w-28">
                      Current org
                    </dt>
                    <dd className="text-hoop-text font-medium">
                      {coach.current_team}
                    </dd>
                  </div>
                )}
                {/* Migration 0022 — level and institution are independent
                    rows; either renders without the other. NULL level means
                    "not stated" and shows nothing at all. */}
                {coach.education_level && (
                  <div className="flex items-baseline gap-3">
                    <dt className="text-hoop-text-3 shrink-0 w-28">
                      Education
                    </dt>
                    <dd className="text-hoop-text font-medium">
                      {coach.education_level}
                    </dd>
                  </div>
                )}
                {coach.education && (
                  <div className="flex items-baseline gap-3">
                    {/* C5 — matches the form's label exactly. */}
                    <dt className="text-hoop-text-3 shrink-0 w-28">
                      School / Institution
                    </dt>
                    <dd className="text-hoop-text font-medium">
                      {coach.education}
                    </dd>
                  </div>
                )}
                {(coach.languages?.length ?? 0) > 0 && (
                  <div className="flex items-baseline gap-3">
                    <dt className="text-hoop-text-3 shrink-0 w-28">
                      Languages
                    </dt>
                    {/* Pills, matching how player languages render in
                        IdentityCard. */}
                    <dd className="flex flex-wrap gap-1.5">
                      {coach.languages?.map((l) => (
                        <span
                          key={l}
                          className="inline-block rounded-md bg-hoop-accent-light px-2 py-0.5 text-xs font-semibold text-hoop-accent"
                        >
                          {l}
                        </span>
                      ))}
                    </dd>
                  </div>
                )}
              </dl>
            </Card>
          )}
        </div>
        <div className="md:col-span-2 space-y-6">
          {/* Migration 0018 — renders values (Pro viewer), the blurred
              teaser (locked), or nothing (no contact set). */}
          <CoachContactCard
            contact={coach.contact}
            contactLocked={coach.contact_locked}
            viewerIsCoach={viewerIsCoach}
          />
          <SocialIcons
            instagramUrl={coach.instagram_url}
            youtubeUrl={coach.youtube_url}
            facebookUrl={coach.facebook_url}
          />
        </div>
      </div>
    </div>
  )
}

function Card({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-lg p-6">
      <h2 className="text-sm font-semibold text-hoop-text uppercase tracking-wider mb-3">
        {title}
      </h2>
      {children}
    </section>
  )
}
